// App router provider — @refinedev/react-router with app-only URL params
// hidden from Refine.
//
// Refine's `useMeta` spreads `useParsed().params` (the WHOLE query string,
// minus filters/sorters/currentPage/pageSize) into the meta object that is
// part of EVERY data-hook query key. The DS tables persist their UI state to
// the URL as a `tbl_<key>` JSON param (useTableUrlState) that changes on
// every search keystroke / sort / page — so with the stock provider each
// write re-keyed every mounted list query on the page: cold cache, spurious
// refetch, staleTime never applying (measured: the queue's employees lookup
// fetched 13-14× per page load despite staleTime 4h). Stripping the app-only
// params here fixes the whole class at the source; Refine's own URL params
// (filters/sorters/currentPage/pageSize for syncWithLocation, `to` for the
// login redirect, route path params like `id`) pass through untouched.

import { useCallback } from "react";
import routerProvider from "@refinedev/react-router";

// Params the app writes for its own UI state — never meaningful to Refine.
// `tbl_<key>`: per-table filter/sort/page persistence (useTableUrlState).
const APP_ONLY_PARAM_RE = /^tbl_/;

export function omitAppOnlyParams(
  params: Record<string, unknown>,
): Record<string, unknown> {
  const entries = Object.entries(params).filter(
    ([key]) => !APP_ONLY_PARAM_RE.test(key),
  );
  // Identity-preserving when nothing matches, so `useParsed` consumers keyed
  // on the params object don't churn.
  return entries.length === Object.keys(params).length
    ? params
    : Object.fromEntries(entries);
}

// The stock provider always ships `parse`; failing loud beats a silent
// provider without URL parsing (every data hook would lose its meta).
const baseParse = ((): NonNullable<typeof routerProvider.parse> => {
  const parse = routerProvider.parse;
  if (!parse) throw new Error("appRouterProvider: @refinedev/react-router has no parse()");
  return parse;
})();

// Hook-named so rules-of-hooks accepts the inner hook calls; assigned to the
// provider's `parse` slot below (Refine calls it as a hook).
function useAppParse(): ReturnType<typeof baseParse> {
  const parseBase = baseParse();
  return useCallback(() => {
    const parsed = parseBase();
    if (!parsed.params) return parsed;
    return { ...parsed, params: omitAppOnlyParams(parsed.params) };
  }, [parseBase]);
}

export const appRouterProvider: typeof routerProvider = {
  ...routerProvider,
  parse: useAppParse,
};
